import React from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string, date } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import States from '../../../Helpers/Lists/States';
import { InputMaskDate, InputMaskPhone } from '../../../Helpers/Formatters';
import { TextInput, SelectInput } from '../../../Helpers/Inputs';
import GenderButtons from '../../Common/SubComponents/GenderButtons';
import BackNext from '../../Common/SubComponents/BackNext';
import { customValidators } from '../../../Helpers/Validators/customValidators'
import '../../Common/BasicInfo.scss';

export default function BasicInfo(props) {

  const formMethods = useForm({
    resolver: yupResolver(BasicInfo.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState
  });

  const { handleSubmit } = formMethods;

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
  };

  return (
    <div className="basic-info d-flex justify-content-center">
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(props.onNext)}>
          <p className="iam mx-auto">{props.juviMode ? "My Child is..." : "I am..."}</p>

          <GenderButtons
            name='gender'
            selectedGender={props.formState.gender}
            onClickMale={() => handleValueChange('gender', 'M')}
            onClickFemale={() => handleValueChange('gender', 'F')}
          />

          <Row>
            <Col>
              <div className='mb-3'>
                <TextInput
                  name="firstName"
                  id="first-name"
                  label="First Name"
                  value={props.formState.firstName}
                  onChange={(e) => handleValueChange('firstName', e.target.value)}
                />
              </div>
            </Col>

            <Col>
              <div className='mb-3'>
                <TextInput
                  name="lastName"
                  id="last-name"
                  label="Last Name"
                  value={props.formState.lastName}
                  onChange={(e) => handleValueChange('lastName', e.target.value)}
                />
              </div>
            </Col>
          </Row>

          <div className='mb-3'>
            <TextInput
              name="birthdate"
              id="birthdate"
              label="Date of Birth"
              value={props.formState.birthdate}
              onChange={(e) => handleValueChange('birthdate', e.target.value)}
              InputProps={{
                inputComponent: InputMaskDate,
              }}
            />
          </div>

          <div className='mb-3'>
            <SelectInput
              name="state"
              id="state"
              value={props.formState.state}
              onChange={(e) => handleValueChange('state', e.target.value)}
              label="State"
              selectOptions={States()}
            />
          </div>

          <div className='mb-3'>
            <TextInput
              name="email"
              id="email"
              label="Email"
              value={props.formState.email}
              onChange={(e) => handleValueChange('email', e.target.value)}
            />
          </div>

          <div className='mb-3'>
            <TextInput
              name="phone"
              id="phone"
              label="Phone Number"
              value={props.formState.phone}
              onChange={(e) => handleValueChange('phone', e.target.value)}
              InputProps={{
                inputComponent: InputMaskPhone,
              }}
            />
          </div>

          <BackNext
            hideBack={props.hideBack}
            onBack={props.onBack}
            continueLabel={{ text: "Contact Our Team" }}
          />
        </Form>
      </FormProvider>

    </div>
  );
}

BasicInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBack: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired
};

BasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    firstName: string().required('First Name is required.').matches(/^[a-zA-Z]+$/, 'Invalid First Name'),
    lastName: string().required('Last Name is required.').matches(/^[a-zA-Z]+$/, 'Invalid Last Name'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min }), `Minimum age is ${ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    email: string().email('Invalid Email Address.').required('Email Address is required.'),
    phone: customValidators.string()
      .isValidPhone('Phone Number must be formatted as "(123) 456-7890".')
      .required('Phone Number is required.'),
  });
};

BasicInfo.DefaultFormState = (prefill = false) => {
  return {
    gender: prefill ? 'M' : '',
    firstName: prefill ? 'John' : '',
    lastName: prefill ? 'Doe' : '',
    birthdate: prefill ? '11/15/1970' : '',
    state: prefill ? 'ID' : '',
    email: prefill ? 'user@address.com' : '',
    phone: prefill ? 2081235555 : '',
  };
};
